<template>
  <div
    class="combobox"
    :class="comboboxClasses"
    @keydown="handleKeydown"
  >
    <div class="combobox__input-wrapper">
      <TextInput
        ref="input"
        :value="displayValue"
        :placeholder="placeholder"
        :is-disabled="isDisabled"
        :is-read-only="isReadOnly"
        :mask="mask"
        :limit="limit"
        :error-messages="errorMessages"
        autocomplete="off"
        @input="handleInputChange"
        @focus="handleInputFocus"
        @blur="handleInputBlur"
      />

      <SpinnerLoader
        v-if="isSearching"
        class="combobox__loader"
      />

      <slot
        name="toggle"
        :toggle="toggleDropdown"
        :is-open="isDropdownOpen"
      >
        <button
          v-if="!isSearching"
          type="button"
          class="combobox__toggle"
          :disabled="isDisabled"
          tabindex="-1"
          @click="toggleDropdown"
        >
        </button>
      </slot>
    </div>

    <transition name="combobox-dropdown">
      <div
        v-if="shouldShowDropdown"
        class="combobox__dropdown"
        :class="`combobox__dropdown--${dropdownDirection}`"
        :style="{ top: dropdownTop + 'px' }"
      >
        <slot
          name="dropdown"
          :options="filteredOptions"
          :select="selectOption"
          :focused-index="focusedIndex"
          :is-selected="isSelected"
        >
          <div class="combobox__options">
            <div
              v-for="(option, index) in filteredOptions"
              :key="getOptionValue(option)"
              class="combobox__option"
              :class="{
                'is-selected': isSelected(option),
                'is-focused': index === focusedIndex
              }"
              @click="selectOption(option)"
              @mouseenter="focusedIndex = index"
            >
              <slot name="option" :option="option" :index="index">
                {{ getOptionLabel(option) }}
              </slot>
            </div>
          </div>

          <div
            v-if="filteredOptions.length === 0"
            class="combobox__empty"
          >
            {{ emptyText }}
          </div>
        </slot>
      </div>
    </transition>
  </div>
</template>

<script>
import { clsx } from 'clsx';
import { EventBus } from '@/shared/lib/event-bus';
import { default as TextInput } from '@/shared/ui/form/inputs/text-input';
import { SpinnerLoader } from '@/shared/ui/loader';

const MINIMAL_DROPDOWN_LIST_HEIGHT = 220;
const DEFAULT_SEARCH_DELAY = 500;

export default {
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: [String, Number, Object, null],
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    mask: {
      type: [String, Function, Boolean],
      default: null,
    },
    searchDelay: {
      type: Number,
      default: DEFAULT_SEARCH_DELAY,
    },
    searchThreshold: {
      type: Number,
      default: 0,
    },
    filterMode: {
      type: String,
      default: 'remote',
      validator: (v) => ['local', 'remote'].includes(v),
    },
    options: {
      type: Array,
      default: () => [],
    },
    valueKey: {
      type: String,
      default: 'id',
    },
    labelKey: {
      type: String,
      default: 'label',
    },
    displayKey: {
      type: String,
      default: null,
    },
    dropdownPosition: {
      type: String,
      default: 'auto',
      validator: (v) => ['auto', 'top', 'bottom'].includes(v),
    },
    hideDropdownWhenEmpty: {
      type: Boolean,
      default: true,
    },
    emptyText: {
      type: String,
      default: 'No results found',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
    limit: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      internalInputValue: '',
      selectedOption: null,

      isDropdownOpen: false,
      isFocused: false,
      focusedIndex: -1,

      dropdownTop: 0,
      dropdownDirection: 'bottom',

      searchTimeoutId: null,
      lastSearchQuery: '',
    };
  },
  mounted() {
    this.initializeFromValue();
    document.addEventListener('click', this.handleClickOutside);
    EventBus.$on('selector:opened', this.closeDropdown);
  },
  beforeDestroy() {
    this.clearSearchTimeout();
    document.removeEventListener('click', this.handleClickOutside);
    EventBus.$off('selector:opened', this.closeDropdown);
  },
  components: { TextInput, SpinnerLoader },
  computed: {
    comboboxClasses() {
      return clsx('combobox', {
        'combobox--disabled': this.isDisabled,
        'combobox--has-error': this.hasError,
        'combobox--open': this.isDropdownOpen,
      });
    },
    displayValue() {
      if (this.selectedOption && !this.isFocused) {
        return this.getOptionDisplayValue(this.selectedOption);
      }

      return this.internalInputValue;
    },
    isSearching() {
      return this.isLoading || this.searchTimeoutId !== null;
    },
    filteredOptions() {
      const safeOptions = this.options.filter(opt => opt != null);
      if (this.filterMode === 'remote') {
        return safeOptions;
      }

      if (!this.internalInputValue) {
        return safeOptions;
      }

      const query = this.internalInputValue.toLowerCase();
      return safeOptions.filter(option =>
        this.getOptionLabel(option).toLowerCase().includes(query)
      );
    },
    shouldShowDropdown() {
      if (!this.isDropdownOpen) return false;
      if (this.isSearching) return false;
      if (this.internalInputValue.length < this.searchThreshold) return false;
      if (this.hideDropdownWhenEmpty && this.filteredOptions.length === 0) return false;
      return true;
    },
    hasError() {
      return this.errorMessages.length > 0;
    },
  },
  methods: {
    initializeFromValue() {
      if (this.value == null || this.value === '') {
        this.resetInternalState();
        return;
      }

      if (typeof this.value === 'object') {
        this.selectedOption = this.value;
        this.internalInputValue = this.getOptionDisplayValue(this.value);
        return;
      }

      const matchingOption = this.options.find(opt =>
        this.getOptionValue(opt) === this.value
      );

      if (matchingOption) {
        this.selectedOption = matchingOption;
        this.internalInputValue = this.getOptionDisplayValue(matchingOption);
      } else {
        this.selectedOption = null;
        this.internalInputValue = String(this.value);

        if (this.filterMode === 'remote' && this.internalInputValue) {
          this.scheduleSearch(this.internalInputValue);
        }
      }
    },
    resetInternalState() {
      this.internalInputValue = '';
      this.selectedOption = null;
    },
    getOptionValue(option) {
      return option && typeof option === 'object' ? option[this.valueKey] : option;
    },
    getOptionLabel(option) {
      if (!option) return '';
      return typeof option === 'object' ? option[this.labelKey] : String(option);
    },
    getOptionDisplayValue(option) {
      if (!option) return '';
      if (this.displayKey && typeof option === 'object') {
        return option[this.displayKey] || '';
      }
      return this.getOptionLabel(option);
    },
    isSelected(option) {
      if (!this.selectedOption) return false;
      return this.getOptionValue(option) === this.getOptionValue(this.selectedOption);
    },
    handleInputFocus(event) {
      if (this.isDisabled || this.isReadOnly) return;

      if (this.selectedOption) {
        this.internalInputValue = this.getOptionDisplayValue(this.selectedOption);
      }

      this.isFocused = true;

      this.$nextTick(() => {
        const inputEl = this.$refs.input?.$refs?.input;
        if (inputEl) {
          inputEl.select();
        }
      });

      this.openDropdown();

      this.$emit('focus', event);
    },
    handleInputBlur(event) {
      this.isFocused = false;

      setTimeout(() => {
        if (!this.$el.contains(document.activeElement)) {
          this.closeDropdown();

          if (!this.selectedOption && (!this.internalInputValue || this.internalInputValue.trim() === '')) {
            const currentValue = this.getOptionValue(this.selectedOption);
            if (currentValue !== null) {
              this.emitValue(null);
            }
          } else {
            this.reconcileInputValue();
          }

          this.$emit('blur', event);
        }
      }, 150);
    },
    handleInputChange(inputValue) {
      const trimmedInput = (inputValue || '').trim();
      const displayValue = this.selectedOption
        ? this.getOptionDisplayValue(this.selectedOption)
        : '';

      if (this.selectedOption && displayValue && trimmedInput === displayValue) {
        return;
      }

      this.internalInputValue = inputValue;

      if (this.selectedOption && trimmedInput !== displayValue) {
        this.selectedOption = null;
        this.focusedIndex = -1;
      }

      this.$emit('input-change', inputValue);

      if (!trimmedInput) {
        this.clearSearchTimeout();
        this.closeDropdown();
        this.emitValue(null);
        return;
      }

      this.openDropdown();
      this.scheduleSearch(trimmedInput);
    },
    emitValue(option) {
      const valueToEmit = option ? this.getOptionValue(option) : null;
      const labelToEmit = option ? this.getOptionLabel(option) : null;

      this.$emit('input', valueToEmit);

      this.$emit('change', {
        value: valueToEmit,
        option: option,
        label: labelToEmit,
      });

      if (option) {
        this.$emit('select', option);
      }
    },
    selectOption(option) {
      this.selectedOption = option;
      this.internalInputValue = this.getOptionDisplayValue(option);
      this.focusedIndex = -1;
      this.closeDropdown();
      this.emitValue(option);
    },
    reconcileInputValue() {
      if (this.selectedOption) {
        this.internalInputValue = this.getOptionDisplayValue(this.selectedOption);
        return;
      }

      if (!this.internalInputValue || this.internalInputValue.trim() === '') {
        if (this.getOptionValue(this.selectedOption) !== null) {
          this.emitValue(null);
        }
      }
    },
    scheduleSearch(query) {
      this.clearSearchTimeout();

      const trimmedQuery = (query || '').trim();
      if (!trimmedQuery) {
        return;
      }

      if (trimmedQuery === this.lastSearchQuery) {
        return;
      }

      this.lastSearchQuery = trimmedQuery;

      if (this.filterMode === 'remote') {
        this.searchTimeoutId = setTimeout(() => {
          this.searchTimeoutId = null;
          this.$emit('search', trimmedQuery);
        }, this.searchDelay);
      }
    },
    clearSearchTimeout() {
      if (this.searchTimeoutId) {
        clearTimeout(this.searchTimeoutId);
        this.searchTimeoutId = null;
      }
    },
    openDropdown() {
      if (this.isDisabled || this.isReadOnly) return;

      EventBus.$emit('selector:opened');

      this.$nextTick(() => {
        this.isDropdownOpen = true;
        this.computeDropdownPosition();

        if (this.selectedOption) {
          this.focusedIndex = this.filteredOptions.findIndex(opt =>
            this.isSelected(opt)
          );
        }
      });
    },
    closeDropdown() {
      this.isDropdownOpen = false;
      this.focusedIndex = -1;
    },
    toggleDropdown() {
      this.isDropdownOpen ? this.closeDropdown() : this.openDropdown();
    },
    computeDropdownPosition() {
      if (this.dropdownPosition !== 'auto') {
        this.dropdownDirection = this.dropdownPosition;
        return;
      }

      const wrapper = this.$el;
      if (!wrapper) {
        this.dropdownDirection = 'bottom';
        return;
      }

      const rect = wrapper.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      this.dropdownDirection =
        spaceBelow >= MINIMAL_DROPDOWN_LIST_HEIGHT || spaceBelow >= spaceAbove
          ? 'bottom'
          : 'top';

      if (this.dropdownDirection === 'bottom') {
        this.dropdownTop = rect.height - 5;
      } else {
        this.$nextTick(() => {
          const dropdown = this.$el.querySelector('.combobox__dropdown');
          this.dropdownTop = dropdown ? -dropdown.offsetHeight + 5 : 0;
        });
      }
    },
    handleKeydown(event) {
      if (this.isDisabled || this.isReadOnly) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.navigateOptions(1);
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.navigateOptions(-1);
          break;

        case 'Enter':
          event.preventDefault();
          this.selectFocusedOption();
          break;

        case 'Escape':
          this.closeDropdown();
          break;

        case 'Tab':
          this.closeDropdown();
          break;
      }
    },
    navigateOptions(direction) {
      const len = this.filteredOptions.length;
      if (len === 0) return;

      if (!this.isDropdownOpen) {
        this.openDropdown();
        return;
      }

      let newIndex = this.focusedIndex + direction;

      if (newIndex < 0) newIndex = len - 1;
      if (newIndex >= len) newIndex = 0;

      this.focusedIndex = newIndex;
      this.scrollToFocused();
    },
    selectFocusedOption() {
      if (this.focusedIndex >= 0 && this.focusedIndex < this.filteredOptions.length) {
        this.selectOption(this.filteredOptions[this.focusedIndex]);
      }
    },
    scrollToFocused() {
      this.$nextTick(() => {
        const option = this.$el.querySelector('.combobox__option.is-focused');
        const dropdown = this.$el.querySelector('.combobox__dropdown');

        if (option && dropdown) {
          const optionTop = option.offsetTop;
          const optionHeight = option.offsetHeight;
          const dropdownHeight = dropdown.clientHeight;

          dropdown.scrollTop = optionTop - dropdownHeight / 2 + optionHeight / 2;
        }
      });
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    },
    clear() {
      this.internalInputValue = '';
      this.selectedOption = null;
      this.lastSearchQuery = '';
      this.closeDropdown();
      this.emitValue(null);

      this.$nextTick(() => {
        if (this.$refs.input) {
          this.$refs.input.value = '';
        }
      });
    },
    setValue(option) {
      if (!option) {
        this.clear();
        return;
      }

      this.selectedOption = option;
      this.internalInputValue = this.getOptionDisplayValue(option);
    },
    focus() {
      this.$refs.input?.focus();
    },
    blur() {
      this.$refs.input?.blur();
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue, oldValue) {
        if (this.isFocused) {
          return;
        }

        if (oldValue === undefined && newValue === null) {
          return;
        }

        this.initializeFromValue();
      },
    },
    options: {
      deep: true,
      handler(newOptions) {
        if (this.value && !this.selectedOption && typeof this.value !== 'object') {
          const matchingOption = newOptions.find(opt =>
            this.getOptionValue(opt) === this.value
          );
          if (matchingOption) {
            this.selectedOption = matchingOption;
            this.internalInputValue = this.getOptionDisplayValue(matchingOption);
          }
        }

        if (this.selectedOption) {
          const updated = newOptions.find(opt =>
            this.getOptionValue(opt) === this.getOptionValue(this.selectedOption)
          );
          if (updated) {
            this.selectedOption = updated;
            this.internalInputValue = this.getOptionDisplayValue(updated);
          }
        }
      },
    },
    isLoading(isLoading, wasLoading) {
      if (this.filterMode === 'remote' && wasLoading && !isLoading) {
        if (this.internalInputValue && this.options.length > 0 && this.isFocused) {
          this.openDropdown();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.combobox {
  position: relative;
  width: 100%;

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__loader {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 20px;
    height: 20px;
    pointer-events: none;
  }

  &__toggle {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #71717A;
    transition: color 0.2s, transform 0.2s;

    &:hover:not(:disabled) {
      color: #09090B;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .is-open {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1000;
    background-color: #ffffff;
    border: 1px solid #E4E4E7;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    max-height: 300px;
    overflow-y: auto;
    margin-top: 4px;

    &--top {
      bottom: 100%;
      margin-bottom: 4px;
      margin-top: 0;
    }

    &--bottom {
      top: 100%;
    }

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f3f4f6;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;

      &:hover {
        background: #9ca3af;
      }
    }
  }

  &__options {
    padding: 4px 0;
  }

  &__option {
    padding: 10px 16px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    font-size: 14px;
    color: #09090B;

    &:hover,
    &.is-focused {
      background-color: #F4F4F5;
    }

    &.is-selected {
      background-color: #EEF2FF;
    }
  }

  &__empty {
    padding: 16px;
    text-align: center;
    color: #71717A;
    font-size: 14px;
  }
}

.combobox-dropdown-enter-active,
.combobox-dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.combobox-dropdown-enter,
.combobox-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
